/**
 * Header component with navigation and user menu
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, BookOpen, Play, GraduationCap, PenTool, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@stores/authStore';
import Button, { IconButton } from '@components/ui/Button';
import { clsx } from 'clsx';

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout, getUserName } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHomeworkMenuOpen, setIsHomeworkMenuOpen] = useState(false);

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  const navigation = [
    { name: 'Home', path: '/', icon: BookOpen },
    { name: 'LEARN', path: '/browse', icon: Play },
    { name: 'Exercises', path: '/exercises', icon: PenTool },
    { name: 'Profile', path: '/profile', icon: User },
  ];

  const homeworkMenuItems = [
    { name: 'Daily Homework', path: '/school-homework' },
    { name: 'Homework Assistant', path: '/homework' },
  ];

  const isHomeworkActive = () => {
    return isActivePath('/homework') || isActivePath('/school-homework');
  };

  return (
    <header className="bg-white shadow-soft border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and brand */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">🦕</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                  Dino Learn
                </h1>
                <p className="text-xs text-gray-500 -mt-1">Interactive Learning</p>
              </div>
            </Link>
          </div>

          {/* Desktop navigation */}
          {isAuthenticated && (
            <nav className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={clsx(
                      'nav-link flex items-center space-x-2',
                      isActivePath(item.path) 
                        ? 'nav-link-active' 
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              
              {/* Homework Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsHomeworkMenuOpen(!isHomeworkMenuOpen)}
                  className={clsx(
                    'nav-link flex items-center space-x-2',
                    isHomeworkActive()
                      ? 'nav-link-active' 
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <GraduationCap className="w-4 h-4" />
                  <span>Homework</span>
                  <ChevronDown className="w-3 h-3" />
                </button>

                {/* Homework Dropdown Menu */}
                {isHomeworkMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setIsHomeworkMenuOpen(false)}
                    />
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-strong border border-gray-200 py-2 z-20">
                      {homeworkMenuItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={clsx(
                            'flex items-center space-x-2 px-4 py-2 text-sm transition-colors',
                            isActivePath(item.path)
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          )}
                          onClick={() => setIsHomeworkMenuOpen(false)}
                        >
                          <span>{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </nav>
          )}

          {/* User menu and mobile menu button */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                {/* User menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-600" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700">
                      {getUserName()}
                    </span>
                  </button>

                  {/* User dropdown menu */}
                  {isUserMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10"
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-strong border border-gray-200 py-2 z-20">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900">{getUserName()}</p>
                          <p className="text-xs text-gray-500 break-all">{user?.email}</p>
                        </div>
                        
                        <Link
                          to="/profile"
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Profile Settings</span>
                        </Link>
                        
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile menu button */}
                <IconButton
                  icon={isMobileMenuOpen ? <X /> : <Menu />}
                  variant="ghost"
                  className="md:hidden"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                />
              </>
            ) : (
              /* Auth buttons for non-authenticated users */
              <div className="flex items-center space-x-3">
                <Link to="/signin">
                  <Button variant="ghost" size="sm">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="primary" size="sm">
                    New Sign up
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile navigation menu */}
        {isAuthenticated && isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <nav className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={clsx(
                      'flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors',
                      isActivePath(item.path)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
              
              {/* Homework Section in Mobile */}
              <div className="px-3 py-2">
                <div className="flex items-center space-x-3 mb-2">
                  <GraduationCap className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-900">Homework</span>
                </div>
                <div className="ml-8 space-y-1">
                  {homeworkMenuItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={clsx(
                        'flex items-center px-3 py-2 rounded-lg transition-colors text-sm',
                        isActivePath(item.path)
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
